import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Timeline,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleX,
  IconDotsVertical,
  IconPlayerSkipForward,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  formatDate,
  hireFeedbackNotes,
  HIRE_FEEDBACK_BANNER_TITLE,
  OUTCOME_LABELS,
  REJECTION_BANNER_TITLE,
  STAGE_HISTORY_TITLE,
  STAGE_TYPE_LABELS,
} from "../lib/format";
import type { Stage, StageEvent, SubmissionStatus } from "../types/api";
import { axioTokens } from "../theme";

export type StageState = "completed" | "current" | "upcoming" | "skipped";

function getSkipReason(stageId: string, history: StageEvent[]): string | null {
  const event = history.find((h) => h.stage_id === stageId && h.outcome === "skipped");
  return event?.notes ?? null;
}

function getStageState(
  stage: Stage,
  index: number,
  stages: Stage[],
  currentStageId: string | null,
  history: StageEvent[],
  status: string,
): StageState {
  const currentIndex = currentStageId ? stages.findIndex((s) => s.id === currentStageId) : -1;

  if (status === "hired") {
    return index <= currentIndex || currentIndex === -1 ? "completed" : "upcoming";
  }

  if (currentIndex === -1) return "upcoming";
  if (index === currentIndex) return "current";
  if (index < currentIndex) {
    const wasSkipped = history.some((h) => h.stage_id === stage.id && h.outcome === "skipped");
    return wasSkipped ? "skipped" : "completed";
  }
  return "upcoming";
}

function stateColor(state: StageState): string {
  switch (state) {
    case "completed":
      return "green";
    case "current":
      return "violet";
    case "skipped":
      return "orange";
    default:
      return "gray";
  }
}

function StateIcon({ state }: { state: StageState }) {
  switch (state) {
    case "completed":
      return <IconCircleCheck size="var(--axio-unit-18)" />;
    case "current":
      return <IconArrowRight size="var(--axio-unit-18)" />;
    case "skipped":
      return <IconPlayerSkipForward size="var(--axio-unit-18)" />;
    default:
      return <IconCircleDashed size="var(--axio-unit-18)" />;
  }
}

interface StageProgressRailProps {
  stages: Stage[];
  currentStageId: string | null;
  status: string;
  history: StageEvent[];
  interactive?: boolean;
  onAdvance?: (nextStage: Stage) => void;
  onSkip?: (nextStage: Stage) => void;
  onMoveToStage?: (stage: Stage) => void;
  loading?: boolean;
}

export function StageProgressRail({
  stages,
  currentStageId,
  status,
  history,
  interactive = false,
  onAdvance,
  onSkip,
  onMoveToStage,
  loading = false,
}: StageProgressRailProps) {
  const currentIndex = currentStageId ? stages.findIndex((s) => s.id === currentStageId) : -1;
  const nextStage =
    currentIndex >= 0 && currentIndex < stages.length - 1 ? stages[currentIndex + 1] : null;
  const isInProgress = status === "in_progress";
  const locked = status === "pending_hr_review" || status === "hired" || status === "rejected" || status === "withdrawn";
  const showActions = interactive && isInProgress && !locked;

  const stageCards = stages.map((stage, index) => {
    const state = getStageState(stage, index, stages, currentStageId, history, status);
    const isLast = index === stages.length - 1;
    const awaitingHr = status === "pending_hr_review" && isLast && state === "current";
    const skipReason = state === "skipped" ? getSkipReason(stage.id, history) : null;

    return (
      <Paper
        key={stage.id}
        withBorder
        p="sm"

        style={{
          minWidth: "var(--axio-unit-140)",
          flex: "0 0 auto",
          borderColor:
            state === "current"
              ? "var(--mantine-color-violet-5)"
              : state === "completed"
                ? "var(--mantine-color-green-3)"
                : state === "skipped"
                  ? "var(--mantine-color-orange-3)"
                  : undefined,
          background:
            state === "current"
              ? "var(--mantine-color-violet-0)"
              : state === "completed"
                ? "var(--mantine-color-green-0)"
                : state === "skipped"
                  ? "var(--mantine-color-orange-0)"
                  : undefined,
          opacity: locked && state === "upcoming" ? 0.6 : 1,
        }}
      >
        <Stack gap="var(--axio-unit-6)">
          <Group justify="space-between" wrap="nowrap">
            <Badge size="xs" circle color={stateColor(state)}>
              {index + 1}
            </Badge>
            <StateIcon state={state} />
          </Group>
          <Text size="sm" fw="var(--axio-font-weight-semibold)" lineClamp={2}>
            {stage.name}
          </Text>
          <Badge size="xs" variant="light" color="gray">
            {STAGE_TYPE_LABELS[stage.stage_type]}
          </Badge>
          {awaitingHr && (
            <Badge size="xs" color="orange" variant="filled">
              Awaiting HR
            </Badge>
          )}
          {state === "skipped" && (
            <Stack gap="var(--axio-unit-2)">
              <Text size="xs" c="orange" fw="var(--axio-font-weight-semibold)">
                Skipped
              </Text>
              {skipReason && (
                <Text size="xs" c="dimmed" lineClamp={3}>
                  {skipReason}
                </Text>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  });

  return (
    <Stack gap="md">
      <Box hiddenFrom="sm">
        <Stack gap="sm">{stageCards}</Stack>
      </Box>
      <Box visibleFrom="sm">
        <ScrollArea type="auto" offsetScrollbars>
          <Group gap="sm" wrap="nowrap" pb="var(--axio-unit-4)">
            {stageCards}
          </Group>
        </ScrollArea>
      </Box>

      {showActions && (
        <Group>
          {nextStage && onAdvance && (
            <Button color="violet" loading={loading} onClick={() => onAdvance(nextStage)}>
              Complete & advance
            </Button>
          )}
          {nextStage && onSkip && (
            <Button
              variant="outline"
              color="orange"
              loading={loading}
              leftSection={<IconPlayerSkipForward size="var(--axio-unit-16)" />}
              onClick={() => onSkip(nextStage)}
            >
              Skip stage
            </Button>
          )}
          {onMoveToStage && (
            <Menu shadow="md" width={axioTokens.unit[220]}>
              <Menu.Target>
                <Button variant="default" rightSection={<IconDotsVertical size="var(--axio-unit-16)" />} loading={loading}>
                  Move to stage
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {stages.map((stage, index) => {
                  if (stage.id === currentStageId) return null;
                  const isJump = index > currentIndex + 1;
                  const isBack = index < currentIndex;
                  return (
                    <Menu.Item key={stage.id} onClick={() => onMoveToStage(stage)}>
                      {stage.name}
                      {isJump && (
                        <Text component="span" size="xs" c="dimmed" ml="var(--axio-unit-4)">
                          (complete prior)
                        </Text>
                      )}
                      {isBack && (
                        <Text component="span" size="xs" c="dimmed" ml="var(--axio-unit-4)">
                          (back)
                        </Text>
                      )}
                    </Menu.Item>
                  );
                })}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      )}
    </Stack>
  );
}

interface PipelineStepperProps {
  stages: Stage[];
  currentStageId: string | null;
  status: string;
  interactive?: boolean;
  onStageClick?: (stage: Stage) => void;
}

/** @deprecated Use StageProgressRail for richer pipeline UI */
export function PipelineStepper({
  stages,
  currentStageId,
  status,
  history = [],
  interactive = false,
  onAdvance,
  onSkip,
  onMoveToStage,
  loading,
}: PipelineStepperProps & {
  history?: StageEvent[];
  onAdvance?: (nextStage: Stage) => void;
  onSkip?: (nextStage: Stage) => void;
  onMoveToStage?: (stage: Stage) => void;
  loading?: boolean;
}) {
  return (
    <StageProgressRail
      stages={stages}
      currentStageId={currentStageId}
      status={status}
      history={history}
      interactive={interactive}
      onAdvance={onAdvance}
      onSkip={onSkip}
      onMoveToStage={onMoveToStage}
      loading={loading}
    />
  );
}

interface MoveStageModalProps {
  stage: Stage | null;
  opened: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  loading: boolean;
  currentStageId?: string | null;
  stages?: Stage[];
}

export function MoveStageModal({
  stage,
  opened,
  onClose,
  onConfirm,
  loading,
  currentStageId,
  stages = [],
}: MoveStageModalProps) {
  const [notes, setNotes] = useState("");

  const currentIndex = currentStageId ? stages.findIndex((s) => s.id === currentStageId) : -1;
  const targetIndex = stage ? stages.findIndex((s) => s.id === stage.id) : -1;
  const isJump = targetIndex > currentIndex + 1;
  const isBack = targetIndex >= 0 && targetIndex < currentIndex;
  const isAdvance = targetIndex === currentIndex + 1;

  function handleClose() {
    setNotes("");
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isAdvance ? `Complete & advance to ${stage?.name ?? "stage"}` : `Move to ${stage?.name ?? "stage"}`}
      centered
    >
      <Stack>
        {isJump && (
          <Text size="sm" c="dimmed">
            All stages before <strong>{stage?.name}</strong> will be marked as completed.
          </Text>
        )}
        {isBack && (
          <Text size="sm" c="dimmed">
            You are moving the candidate back to an earlier stage.
          </Text>
        )}
        {isAdvance && (
          <Text size="sm" c="dimmed">
            Mark the current stage as completed and move to <strong>{stage?.name}</strong>.
          </Text>
        )}
        {!isJump && !isBack && !isAdvance && (
          <Text size="sm" c="dimmed">
            Move this candidate to the <strong>{stage?.name}</strong> stage.
          </Text>
        )}
        <Textarea
          label="Notes (optional)"
          placeholder="e.g. Screening done on call with HR"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />
        <Button
          color="violet"
          loading={loading}
          onClick={() => {
            onConfirm(notes);
            setNotes("");
          }}
        >
          {isAdvance ? "Complete & advance" : "Confirm move"}
        </Button>
      </Stack>
    </Modal>
  );
}

interface SkipStageModalProps {
  stage: Stage | null;
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}

export function SkipStageModal({ stage, opened, onClose, onConfirm, loading }: SkipStageModalProps) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  function handleClose() {
    setReason("");
    onClose();
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Skip stage" centered>
      <Stack>
        <Text size="sm" c="dimmed">
          Skip the current stage and move to <strong>{stage?.name}</strong>. A reason is required.
        </Text>
        <Textarea
          label="Reason for skip"
          placeholder="e.g. Candidate already completed this step externally"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={2}
          required
          error={reason.length > 0 && trimmed.length < 2 ? "Enter at least 2 characters" : undefined}
        />
        <Button
          color="orange"
          loading={loading}
          disabled={trimmed.length < 2}
          onClick={() => {
            onConfirm(trimmed);
            setReason("");
          }}
        >
          Skip stage
        </Button>
      </Stack>
    </Modal>
  );
}

const FAILED_OUTCOME = "failed";
const SKIPPED_OUTCOME = "skipped";
const REJECTED_STATUS = "rejected";
const HIRED_STATUS = "hired";
const UNKNOWN_STAGE_LABEL = "Stage";

function stageEventActorLine(event: StageEvent): string {
  const parts = [formatDate(event.created_at)];
  if (event.actor_label) {
    parts.push(event.actor_label);
  }
  if (event.actor_name && event.actor_name !== event.actor_label) {
    parts.push(event.actor_name);
  }
  return parts.join(" · ");
}

interface StageHistoryProps {
  history: StageEvent[];
  stages: Stage[];
}

export function StageHistory({ history, stages }: StageHistoryProps) {
  if (history.length === 0) return null;

  return (
    <Paper withBorder p="lg">
      <Title order={5} mb="md">
        {STAGE_HISTORY_TITLE}
      </Title>
      <Timeline active={history.length} bulletSize={axioTokens.unit[22]}>
        {history.map((event) => {
          const stage = stages.find((s) => s.id === event.stage_id);
          const isFail = event.outcome === FAILED_OUTCOME;
          const isSkip = event.outcome === SKIPPED_OUTCOME;
          const outcomeLabel = OUTCOME_LABELS[event.outcome] ?? event.outcome.replace(/_/g, " ");
          return (
            <Timeline.Item
              key={event.id}
              bullet={
                isFail ? (
                  <IconCircleX size="var(--axio-unit-14)" />
                ) : isSkip ? (
                  <IconPlayerSkipForward size="var(--axio-unit-14)" />
                ) : (
                  <IconCircleCheck size="var(--axio-unit-14)" />
                )
              }
              color={isFail ? "red" : isSkip ? "orange" : "green"}
              title={`${stage?.name ?? UNKNOWN_STAGE_LABEL} — ${outcomeLabel}`}
            >
              <Text size="xs" c="dimmed">
                {stageEventActorLine(event)}
              </Text>
              {event.notes ? <Text size="sm">{event.notes}</Text> : null}
            </Timeline.Item>
          );
        })}
      </Timeline>
    </Paper>
  );
}

export function SubmissionDecisionBanners({
  status,
  rejectionReason,
  history,
}: {
  status: SubmissionStatus | string;
  rejectionReason: string | null;
  history: StageEvent[];
}) {
  const hireNotes = status === HIRED_STATUS ? hireFeedbackNotes(history) : null;
  return (
    <>
      {status === REJECTED_STATUS && rejectionReason ? (
        <Alert color="red" title={REJECTION_BANNER_TITLE}>
          {rejectionReason}
        </Alert>
      ) : null}
      {hireNotes ? (
        <Alert color="green" title={HIRE_FEEDBACK_BANNER_TITLE}>
          {hireNotes}
        </Alert>
      ) : null}
    </>
  );
}

export function PendingReviewBanner({ message }: { message?: string }) {
  return (
    <Paper withBorder p="md" bg="orange.0">
      <Text fw="var(--axio-font-weight-semibold)" c="orange.9">
        Awaiting HR confirmation
      </Text>
      <Text size="sm" c="orange.9">
        {message ??
          "This candidate has been submitted for HR review. You cannot edit stages until HR confirms or rejects."}
      </Text>
    </Paper>
  );
}
